import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@pm-next/shared-types';
import { getAccessToken, getRefreshToken, setAccessToken, clearTokens } from '@/auth/token-storage';

const rawApiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const API_BASE =
  typeof rawApiUrl === 'string' && rawApiUrl.trim() !== ''
    ? `${rawApiUrl.replace(/\/$/, '')}/api`
    : '/api';

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers: ((success: boolean) => void)[] = [];

function subscribeToRefresh(callback: (success: boolean) => void) {
  refreshSubscribers.push(callback);
}

function notifyRefreshSubscribers(success: boolean) {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearTokens();
    return false;
  }
  try {
    const response = await axios.post<{ ok: boolean; accessToken?: string }>(
      `${API_BASE}/auth/refresh`,
      { refreshToken }
    );
    if (response.data?.ok === true && response.data.accessToken) {
      setAccessToken(response.data.accessToken);
      return true;
    }
    clearTokens();
    return false;
  } catch {
    clearTokens();
    return false;
  }
}

/**
 * Refresh the access token using the stored refresh token.
 * Coordinates concurrent callers (axios interceptor + SSE fetch) so only one
 * refresh request runs at a time.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeToRefresh(resolve);
    });
  }

  isRefreshing = true;
  let success = false;
  try {
    success = await tryRefreshToken();
    return success;
  } finally {
    // Clear the lock before waking waiters so a late subscriber cannot hang
    // on an empty queue while isRefreshing is still true.
    isRefreshing = false;
    notifyRefreshSubscribers(success);
  }
}

function isNonRefreshableAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return url.includes('/auth/exchange') || url.includes('/auth/refresh');
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isNonRefreshableAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const success = await refreshAccessToken();

      if (success) {
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export async function fetchApi<T>(
  endpoint: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: unknown;
    params?: Record<string, unknown>;
  }
): Promise<T> {
  const { method = 'GET', data, params } = options || {};

  const response = await apiClient.request<T>({
    url: endpoint,
    method,
    data,
    params,
  });

  return response.data;
}

// --- Document ingestion ---

export interface UploadResponse {
  path: string;
  url: string;
}

export async function uploadDocument(file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<UploadResponse>('/storage/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export interface IngestJobListItem {
  id: string;
  file_name: string;
  status: string;
  document_type?: string | null;
  employee_id?: number | null;
  created_at: string;
  completed_at?: string | null;
  total_tokens?: number | null;
  cost?: number | null;
  response_time_ms?: number | null;
  _count: { results: number };
}

export interface IngestJob {
  id: string;
  file_name: string;
  file_url: string;
  mime_type: string;
  status: string;
  document_type?: string | null;
  error_message?: string | null;
  employee_id?: number | null;
  created_at: string;
  total_tokens?: number | null;
  cost?: number | null;
  response_time_ms?: number | null;
  results?: ExtractionResultItem[];
  importableFieldNames?: string[];
}

export interface ExtractionResultItem {
  id: string;
  field_name: string;
  field_value: string | null;
  confidence: number;
  source_text: string | null;
  status: string;
}

export async function createIngestJob(body: {
  fileName: string;
  fileUrl: string;
  mimeType: string;
  employeeId?: number;
}): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>('/ingest', body);
  return response.data;
}

export async function listIngestJobs(params?: {
  status?: string;
  limit?: number;
  includeArchived?: boolean;
}): Promise<IngestJobListItem[]> {
  const response = await apiClient.get<IngestJobListItem[]>('/ingest', {
    params: {
      ...params,
      includeArchived: params?.includeArchived === true ? 'true' : undefined,
    },
  });
  return response.data;
}

export async function getIngestJob(id: string): Promise<IngestJob> {
  const response = await apiClient.get<IngestJob>(`/ingest/${id}`);
  return response.data;
}

export async function getIngestJobDownloadUrl(id: string): Promise<{ url: string }> {
  const response = await apiClient.get<{ url: string }>(`/ingest/${id}/download`);
  return response.data;
}

export async function archiveIngestJob(id: string): Promise<{ archived: boolean }> {
  const response = await apiClient.post<{ archived: boolean }>(`/ingest/${id}/archive`);
  return response.data;
}

export async function applyIngestJob(
  jobId: string,
  body: {
    employeeId: number;
    acceptedFields: Record<string, string>;
    storeDocument?: boolean;
    categoryId?: number;
  }
): Promise<{ applied: number }> {
  const response = await apiClient.post<{ applied: number }>(`/ingest/${jobId}/apply`, body);
  return response.data;
}

// --- Chat attachments (MarkItDown) ---

export interface ChatAttachment {
  id: string;
  status: string;
  fileName: string;
  mimeType: string;
  errorMessage?: string | null;
  markdown?: string | null;
  markdownPreview?: string | null;
}

export async function createChatAttachment(body: {
  fileName: string;
  fileUrl: string;
  mimeType: string;
  conversationId?: number;
}): Promise<ChatAttachment> {
  const response = await apiClient.post<ChatAttachment>('/chat/attachments', body);
  return response.data;
}

export async function getChatAttachment(id: string): Promise<ChatAttachment> {
  const response = await apiClient.get<ChatAttachment>(`/chat/attachments/${id}`);
  return response.data;
}

// --- Employees ---

export interface EmployeeListItem {
  id: number;
  code: string;
  firstname: string;
  lastname: string;
  email: string | null;
  job_post: string | null;
  job_department: string | null;
  departure_date: string | null;
  department?: { id: number; name: string } | null;
}

export interface EmployeesListResponse {
  data: EmployeeListItem[];
  total: number;
}

export async function listEmployees(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<EmployeesListResponse> {
  const response = await apiClient.get<EmployeesListResponse>('/employees', { params });
  return response.data;
}

export async function getEmployee(id: number): Promise<
  EmployeeListItem & {
    phone?: string;
    mobile?: string;
    dob?: string;
    nic?: string;
    address1?: string;
  }
> {
  const response = await apiClient.get(`/employees/${id}`);
  return response.data;
}
